import { Grid } from '@material-ui/core';
import {
  dateFormat,
  DefaultCard,
  DisplayItem,
  FakeInput,
  FormHookResult,
} from 'design-system';
import {
  makeDateField,
  makeDropDown,
  makeMoneyField,
  makeRadioGroupSelector,
  makeSearchableDropDown,
  makeTextField,
} from 'src/components/MakeFormFields';

import genders from 'src/constants/genders';
import { moneyOrigins, moneyTypes } from 'src/constants/money';
import genderString from 'src/functions/genderString';
import { LoadingEquifax } from './components/LoadingEquifax';

export const { dateFromServerString } = dateFormat;

type StaticRowProps = {
  label: string;
  value: string;
};

function makeStaticRow({ label, value }: StaticRowProps): React.ReactNode {
  return (
    <Grid item xs={12} md={6}>
      <FakeInput inputLabel={label} defaultValue={value} />
    </Grid>
  );
}

export function makeCollectiveGeneralDataSection(
  personIsInvalid: boolean,
  getInputProps: FormHookResult,
): React.ReactNode {
  return (
    <Grid item xs={12}>
      <DefaultCard
        data-testid="general-data-card"
        title="Datos del Contratante"
        disabled={personIsInvalid}
      >
        <br />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {makeTextField({
              isDisabled: personIsInvalid,
              label: 'Nombres',
              inputName: 'names',
              inputId: 'swi-general-data-names',
              getInputProps,
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {makeTextField({
              label: 'Apellidos',
              isDisabled: personIsInvalid,
              getInputProps,
              inputName: 'lastNames',
              inputId: 'swi-general-data-lastNames',
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {makeTextField({
              inputName: 'email',
              inputId: 'swi-general-data-email',
              isDisabled: personIsInvalid,
              label: 'Correo Electrónico',
              helperText:
                'Este correo se utilizará para realizar la firma digital',
              getInputProps,
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {makeTextField({
              inputName: 'phoneNumber',
              inputId: 'swi-general-data-phoneNumber',
              label: 'Celular',
              isDisabled: personIsInvalid,
              getInputProps,
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {makeDateField({
              inputName: 'birthday',
              inputId: 'swi-general-data-birthday',
              label: 'Fecha de nacimiento',
              getInputProps,
              isDisabled: personIsInvalid,
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {makeRadioGroupSelector({
              inputId: 'inspection-radio-gender',
              label: 'Género',
              getInputProps,
              inputName: 'gender',
              dropdownList: genders,
              gridSize: 6,
              isDisabled: personIsInvalid,
            })}
          </Grid>
        </Grid>
      </DefaultCard>
    </Grid>
  );
}

type GeneralDataSectionProps = {
  getInputProps: FormHookResult;
  requestingCountries: boolean;
  requestingBirthPlaces: boolean;
  countryOptions: DisplayItem[];
  birthPlaceOptions: DisplayItem[];
  personIsInvalid: boolean;
  disabledCivilStatus: boolean;
  civilStatusOptions: DisplayItem[];
  requestingCivilStatus: boolean;
  isLoading: boolean;
};

export function makeIndividualGeneralDataSection({
  getInputProps,
  requestingCountries,
  requestingBirthPlaces,
  countryOptions,
  birthPlaceOptions,
  personIsInvalid,
  disabledCivilStatus,
  civilStatusOptions,
  requestingCivilStatus,
  isLoading,
}: GeneralDataSectionProps): React.ReactNode {
  if (isLoading)
    return (
      <Grid item xs={12}>
        <LoadingEquifax />
      </Grid>
    );
  return (
    <Grid item xs={12}>
      <DefaultCard
        data-testid="general-data-card"
        title="Datos del Contratante"
        disabled={personIsInvalid}
      >
        <br />
        <Grid container spacing={3}>
          {makeStaticRow({
            label: 'Nombres',
            value: getInputProps('names').value as string,
          })}
          {makeStaticRow({
            label: 'Apellidos',
            value: getInputProps('lastNames').value as string,
          })}
          {makeStaticRow({
            label: 'Fecha de Nacimiento',
            value: dateFromServerString(
              getInputProps('birthday').value as string,
            ),
          })}
          {makeStaticRow({
            label: 'Género',
            value: genderString(getInputProps('gender').value as string),
          })}
          <Grid item xs={12} md={6}>
            {makeSearchableDropDown({
              inputId: 'swi-general-data-birthPlace',
              label: 'País y lugar de nacimiento',
              getInputProps,
              isDisabled: personIsInvalid,
              inputName: 'birthPlace',
              dropdownList: birthPlaceOptions,
              requesting: requestingBirthPlaces,
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {makeSearchableDropDown({
              inputId: 'swi-general-data-otherNationality',
              label: 'Otra nacionalidad (opcional)',
              getInputProps,
              isDisabled: personIsInvalid,
              inputName: 'otherNationality',
              dropdownList: countryOptions,
              requesting: requestingCountries,
            })}
          </Grid>
          {(disabledCivilStatus || getInputProps('idType').value !== 'N') && (
            <Grid item xs={12} md={6}>
              {makeSearchableDropDown({
                inputId: 'swi-general-data-civilStatus',
                label: 'Estado civil',
                getInputProps,
                isDisabled: personIsInvalid,
                inputName: 'civilStatus',
                dropdownList: civilStatusOptions,
                requesting: requestingCivilStatus,
              })}
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            {makeTextField({
              inputName: 'email',
              inputId: 'swi-general-data-email',
              isDisabled: personIsInvalid,
              label: 'Correo Electrónico',
              getInputProps,
              helperText: 'Lo necesitarás para realizar la firma digital',
            })}
          </Grid>
          <Grid item xs={12} md={6}>
            {makeTextField({
              inputName: 'phoneNumber',
              inputId: 'swi-general-data-phoneNumber',
              label: 'Teléfono',
              isDisabled: personIsInvalid,
              getInputProps,
            })}
          </Grid>
        </Grid>
      </DefaultCard>
    </Grid>
  );
}

type OccupationDataProps = {
  getInputProps: FormHookResult;
  countryOptions: DisplayItem[];
  requestingCountries: boolean;
  occupationOptions: DisplayItem[];
  requestingOccupations: boolean;
  isMobile: boolean;
  personIsInvalid: boolean;
  setSelectedOrigin: (value: string) => void;
  isRequiredField: boolean;
  isLoading: boolean;
};
export function makeOccupationDataSection({
  getInputProps,
  countryOptions,
  requestingCountries,
  occupationOptions,
  requestingOccupations,
  personIsInvalid,
  setSelectedOrigin,
  isRequiredField,
  isLoading,
}: OccupationDataProps): React.ReactNode {
  if (isLoading) {
    return (
      <Grid item xs={12}>
        <LoadingEquifax />
      </Grid>
    );
  }
  return (
    <Grid item xs={12}>
      <DefaultCard
        data-testid="ocupation-data-card"
        title="Datos de ocupación o actividad"
      >
        <br />
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            {makeSearchableDropDown({
              inputId: 'sales-web-inspection-originType',
              label: '¿De dónde provienen los fondos?',
              getInputProps,
              dropdownList: moneyOrigins,
              inputName: 'originType',
              requesting: moneyOrigins.length === 0,
              isDisabled: personIsInvalid,
              onUpdate: (value) => setSelectedOrigin(value),
            })}
          </Grid>
          <Grid item container xs={12} md={6} spacing={1}>
            <Grid item xs={8}>
              {makeMoneyField({
                inputId: 'sales-web-inspection-salary',
                label: 'Ingreso mensual',
                getInputProps,
                inputName: 'salary',
                currencyInputName: 'typeOfMoney',
                isDisabled: personIsInvalid,
              })}
            </Grid>
            <Grid item xs={4}>
              {makeDropDown({
                inputId: 'sales-web-inspection-typeOfMoney',
                label: 'Tipo de Moneda',
                getInputProps,
                inputName: 'typeOfMoney',
                dropdownList: moneyTypes,
                isDisabled: personIsInvalid,
              })}
            </Grid>
          </Grid>
          {isRequiredField && (
            <Grid item xs={12} md={6}>
              {makeTextField({
                inputId: 'sales-web-inspection-enterprise',
                label: 'Nombre de la empresa',
                inputName: 'enterprise',
                getInputProps,
                isDisabled: personIsInvalid,
              })}
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            {makeSearchableDropDown({
              inputId: 'sales-web-inspection-originCountry',
              label: 'País de domicilio de la empresa',
              getInputProps,
              inputName: 'originCountry',
              dropdownList: countryOptions,
              requesting: requestingCountries,
              isDisabled: personIsInvalid,
            })}
          </Grid>
          {isRequiredField && (
            <Grid item xs={12} md={6}>
              {makeSearchableDropDown({
                inputId: 'sales-web-inspection-occupation',
                label: 'Profesión u oficio',
                getInputProps,
                inputName: 'occupation',
                dropdownList: occupationOptions,
                requesting: requestingOccupations,
                isDisabled: personIsInvalid,
              })}
            </Grid>
          )}
        </Grid>
      </DefaultCard>
    </Grid>
  );
}
